import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { projects } from '../../assets/projects';
import { interiors } from '../../assets/interiors';
import { initInteriorLightbox } from '../../utils/initInteriorLightbox';
import useReveal from '../../hooks/useReveal';
import {
  gsap,
  ScrollTrigger,
  setupGsap,
  prefersReducedMotion,
} from '../../motion/gsapSetup';

const HOME_VISIBLE = 10;
const HOME_SIZES = [
  'wide',
  'standard',
  'standard',
  'standard',
  'standard',
  'standard',
  'standard',
  'featured',
  'standard',
  'standard',
];

const Work = () => {
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const horizontalRef = useRef(null);
  const interiorsRef = useRef(null);

  const visibleInteriors = interiors.slice(0, HOME_VISIBLE).map((item, index) => ({
    ...item,
    size: HOME_SIZES[index] || 'standard',
  }));

  useReveal(sectionRef, '.interior-tile', {
    y: 32,
    stagger: 0.05,
    start: 'top 90%',
  });
  useEffect(() => initInteriorLightbox(interiorsRef.current, 'a.interior-zoom'), []);

  useEffect(() => {
    setupGsap();
    const section = horizontalRef.current;
    const track = trackRef.current;
    if (!section || !track) return undefined;

    const mm = gsap.matchMedia();

    mm.add('(min-width: 768px)', () => {
      if (prefersReducedMotion()) return;

      const getScrollAmount = () => {
        const trackWidth = track.scrollWidth;
        const viewport = section.offsetWidth;
        return -(trackWidth - viewport + 48);
      };

      gsap.to(track, {
        x: getScrollAmount,
        ease: 'none',
        scrollTrigger: {
          trigger: section,
          start: 'top top',
          end: () => `+=${Math.abs(getScrollAmount())}`,
          pin: true,
          scrub: 0.85,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });
    });

    mm.add('(max-width: 767px)', () => {
      gsap.set(track, { clearProps: 'all' });
    });

    const refreshTimer = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 150);

    return () => {
      clearTimeout(refreshTimer);
      mm.revert();
    };
  }, []);

  return (
    <section id="work" ref={sectionRef}>
      <div className="work-horizontal-section" ref={horizontalRef}>
        <div className="apple-work-head apple-center">
          <div className="apple-wrap">
            <span className="apple-eyebrow">Selected work</span>
            <h2 className="apple-display">
              Our <span className="apple-italic">portfolio</span>
            </h2>
            <p className="apple-lede">
              Residences across Delhi and Noida — scroll to explore the projects.
            </p>
          </div>
        </div>

        <div className="work-horizontal">
          <div className="container">
            <div className="work-horizontal-track" ref={trackRef}>
              {projects.map((item) => (
                <Link key={item.id} to={item.link} className="work-horizontal-card">
                  <img src={item.image} alt={item.title} />
                  <div className="work-horizontal-meta">
                    <span className="num">{item.number}</span>
                    <h3>{item.title}</h3>
                    <span className="loc">{item.location}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="apple-interiors-block">
        <div className="apple-wrap apple-center">
          <span className="apple-eyebrow">Designed living</span>
          <h2 className="apple-display" style={{ fontSize: 'clamp(32px, 5vw, 56px)' }}>
            Interiors
          </h2>
          <p className="apple-lede">
            Material warmth, quiet luxury, and thoughtful light — interiors that
            feel lived-in from day one.
          </p>
        </div>

        <div className="apple-wrap--wide">
          <div ref={interiorsRef} className="interiors-mosaic">
            {visibleInteriors.map((item) => (
              <a
                key={item.id}
                href={item.image}
                className={`interior-tile interior-zoom size-${item.size}`}
                title={item.title}
              >
                <img src={item.image} alt={item.title} loading="lazy" />
                <span className="interior-overlay">
                  <span className="interior-number">{item.number}</span>
                  <span className="interior-label">View</span>
                </span>
              </a>
            ))}
          </div>

          <div className="apple-center">
            <Link to="/interiors" className="interiors-toggle">
              View all interiors ({interiors.length})
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Work;
